// ╔══════════════════════════════════════════════════════════════════════════════╗
// ║  InlineEditor — click-to-edit for editable fields                          ║
// ║                                                                            ║
// ║  Standalone class. Can be extracted to a separate file later.              ║
// ║  Usage: InlineEditor.attach(rootElementId, reactiveState, fields)          ║
// ║                                                                            ║
// ║  Handles:                                                                  ║
// ║    • span.input-value[data-clientstate-bindtype="twoway"] → click → edit   ║
// ║    • Numeric: text input with format-aware parse (percent, number k/m)     ║
// ║    • Dropdown: inline <select> from fields[name].items                     ║
// ║    • Checkbox: toggle boolean value on click                               ║
// ║    • Datepicker: native <input type="date">                                ║
// ║    • Validation with visual feedback (red/pink background)                 ║
// ║    • Keyboard: Enter=commit, Esc=cancel, Tab=next editable cell            ║
// ║    • Writing parsed value back to reactive state → triggers recomputation  ║
// ╚══════════════════════════════════════════════════════════════════════════════╝

export class InlineEditor {

	/**
	 * Attach inline editing to all editable fields inside a root element.
	 * @param {string} rootId — ID of the component root element
	 * @param {object} state — reactive state object (Vue-reactivity proxy)
	 * @param {object} [fields] — field metadata from ctrl.fields (dropdown items, control types)
	 */
	static attach(rootId, state, fields) {
		const root = document.getElementById(rootId);
		if (!root) return;

		// Single delegated listener on the root — no per-cell handlers needed
		root.addEventListener('click', (e) => {
			const span = e.target.closest('.input-value[data-clientstate-bindtype="twoway"]');
			if (!span) return;
			if (span.dataset._ieActive === '1') return; // already editing
			InlineEditor._enterEdit(span, state, fields);
		});
	}

	// ──────────────────────────────────────────────
	//  Enter edit mode: route to appropriate control
	// ──────────────────────────────────────────────

	static _enterEdit(span, state, fields) {
		const attrs = InlineEditor._readAttrs(span);
		if (!attrs) return;

		// Determine control type from fields metadata
		const fieldConfig = fields ? fields[attrs.name] : null;
		const control = fieldConfig ? fieldConfig.control : null;

		switch (control) {
			case 'dropdown':
				InlineEditor._enterDropdownEdit(span, state, attrs, fieldConfig);
				break;
			case 'checkbox':
				InlineEditor._toggleCheckbox(span, state, attrs);
				break;
			case 'datepicker':
				InlineEditor._enterDateEdit(span, state, attrs);
				break;
			default:
				InlineEditor._enterNumericEdit(span, state, attrs);
		}
	}

	// ──────────────────────────────────────────────
	//  Numeric edit: span → text input (existing logic)
	// ──────────────────────────────────────────────

	static _enterNumericEdit(span, state, attrs) {
		const rawValue = InlineEditor._getStateValue(state, attrs);

		const input = document.createElement('input');
		input.type = 'text';
		input.className = 'ie-input';
		input.value = InlineEditor._formatForEdit(rawValue, attrs.format);

		// Match dimensions of the span
		const rect = span.getBoundingClientRect();
		input.style.width = Math.max(rect.width + 8, 48) + 'px';
		input.style.minWidth = '48px';
		input.style.textAlign = getComputedStyle(span).textAlign || 'right';

		// Mark span as editing, hide it, insert input
		span.dataset._ieActive = '1';
		span.style.display = 'none';
		span.parentNode.insertBefore(input, span.nextSibling);

		input.focus();
		input.select();

		// ── Event handlers ──

		const commitAndClose = () => {
			InlineEditor._exitEdit(input, span, state, attrs, false);
		};

		const cancelAndClose = () => {
			InlineEditor._exitEdit(input, span, state, attrs, true);
		};

		input.addEventListener('blur', () => {
			// Defer slightly so that Tab keydown fires before blur removes the input
			setTimeout(() => {
				if (input.parentNode) commitAndClose();
			}, 0);
		});

		input.addEventListener('keydown', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				InlineEditor._exitEdit(input, span, state, attrs, false);
			} else if (e.key === 'Escape') {
				e.preventDefault();
				InlineEditor._exitEdit(input, span, state, attrs, true);
			} else if (e.key === 'Tab') {
				e.preventDefault();
				InlineEditor._exitEdit(input, span, state, attrs, false);
				if (!input.parentNode) {
					// Successfully closed → move to next/prev editable
					InlineEditor._focusNext(span, e.shiftKey);
				}
			}
		});

		// Remove red highlight as user types
		input.addEventListener('input', () => {
			input.classList.remove('ie-input-invalid');
		});
	}

	// ──────────────────────────────────────────────
	//  Dropdown edit: span → select
	// ──────────────────────────────────────────────

	static _enterDropdownEdit(span, state, attrs, fieldConfig) {
		const items = fieldConfig.items;
		if (!items || items.length === 0) return;

		const currentValue = InlineEditor._getStateValue(state, attrs);

		const select = document.createElement('select');
		select.className = 'ie-select';

		for (const item in items) {
			const opt = document.createElement('option');
			opt.value = item;
			opt.textContent = items[item];
			if (String(item) === String(currentValue)) opt.selected = true;
			select.appendChild(opt);
		}

		const rect = span.getBoundingClientRect();
		select.style.minWidth = Math.max(rect.width + 16, 60) + 'px';

		span.dataset._ieActive = '1';
		span.style.display = 'none';
		span.parentNode.insertBefore(select, span.nextSibling);
		select.focus();

		const commit = () => {
			if (!select.parentNode) return;
			let newValue = select.value;
			// Restore original type: if state value was a number, parse back to number
			if (typeof currentValue === 'number') {
				newValue = Number(newValue);
			} else if (typeof currentValue === 'boolean') {
				newValue = newValue === 'true';
			}
			InlineEditor._setStateValue(state, attrs, newValue);
			select.remove();
			delete span.dataset._ieActive;
			span.style.display = '';
		};

		const cancel = () => {
			if (!select.parentNode) return;
			select.remove();
			delete span.dataset._ieActive;
			span.style.display = '';
		};

		select.addEventListener('change', commit);
		select.addEventListener('blur', () => {
			setTimeout(() => { if (select.parentNode) commit(); }, 0);
		});
		select.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') { e.preventDefault(); cancel(); }
			if (e.key === 'Enter') { e.preventDefault(); commit(); }
			if (e.key === 'Tab') {
				e.preventDefault();
				commit();
				InlineEditor._focusNext(span, e.shiftKey);
			}
		});
	}

	// ──────────────────────────────────────────────
	//  Checkbox: toggle value on click
	// ──────────────────────────────────────────────

	static _toggleCheckbox(span, state, attrs) {
		const current = InlineEditor._getStateValue(state, attrs);
		InlineEditor._setStateValue(state, attrs, !current);
		// Reactive effect will update span textContent automatically
	}

	// ──────────────────────────────────────────────
	//  Date edit: span → input[type=date]
	// ──────────────────────────────────────────────

	static _enterDateEdit(span, state, attrs) {
		const rawValue = InlineEditor._getStateValue(state, attrs);

		const input = document.createElement('input');
		input.type = 'date';
		input.className = 'ie-input ie-date';

		if (rawValue) {
			const d = new Date(rawValue);
			if (!isNaN(d.getTime())) {
				const yyyy = d.getFullYear();
				const mm = String(d.getMonth() + 1).padStart(2, '0');
				const dd = String(d.getDate()).padStart(2, '0');
				input.value = `${yyyy}-${mm}-${dd}`;
			}
		}

		const rect = span.getBoundingClientRect();
		input.style.minWidth = Math.max(rect.width + 8, 130) + 'px';

		span.dataset._ieActive = '1';
		span.style.display = 'none';
		span.parentNode.insertBefore(input, span.nextSibling);
		input.focus();

		const commit = () => {
			if (!input.parentNode) return;
			if (input.value) {
				InlineEditor._setStateValue(state, attrs, input.value + 'T00:00:00');
			}
			input.remove();
			delete span.dataset._ieActive;
			span.style.display = '';
		};

		const cancel = () => {
			if (!input.parentNode) return;
			input.remove();
			delete span.dataset._ieActive;
			span.style.display = '';
		};

		input.addEventListener('blur', () => {
			setTimeout(() => { if (input.parentNode) commit(); }, 0);
		});
		input.addEventListener('keydown', (e) => {
			if (e.key === 'Enter') { e.preventDefault(); commit(); }
			if (e.key === 'Escape') { e.preventDefault(); cancel(); }
			if (e.key === 'Tab') {
				e.preventDefault();
				commit();
				InlineEditor._focusNext(span, e.shiftKey);
			}
		});
	}

	// ──────────────────────────────────────────────
	//  Exit edit mode: input → span
	// ──────────────────────────────────────────────

	static _exitEdit(input, span, state, attrs, isCancel) {
		if (!input.parentNode) return; // already removed

		if (!isCancel) {
			const rawInput = input.value.trim();

			if (rawInput !== '') {
				const parsed = InlineEditor._parseValue(rawInput, attrs.format);

				if (parsed === null || !isFinite(parsed)) {
					// Invalid value — highlight and keep input open
					input.classList.add('ie-input-invalid');
					input.focus();
					return;
				}

				// Write to reactive state
				InlineEditor._setStateValue(state, attrs, parsed);
			}
			// Empty string = no change (same as cancel)
		}

		// Remove input, restore span
		input.remove();
		delete span.dataset._ieActive;
		span.style.display = '';
		// Tango reactive effect will update span textContent automatically
	}

	// ──────────────────────────────────────────────
	//  Tab navigation between editable cells
	// ──────────────────────────────────────────────

	static _focusNext(currentSpan, reverse) {
		const root = currentSpan.closest('[data-ctrl]');
		if (!root) return;

		const all = Array.from(
			root.querySelectorAll('.input-value[data-clientstate-bindtype="twoway"]')
		);
		if (all.length === 0) return;

		const idx = all.indexOf(currentSpan);
		if (idx === -1) return;

		const nextIdx = reverse
			? (idx - 1 + all.length) % all.length
			: (idx + 1) % all.length;

		const nextSpan = all[nextIdx];
		if (nextSpan) nextSpan.click();
	}

	// ──────────────────────────────────────────────
	//  Read data-* attributes from a span element
	// ──────────────────────────────────────────────

	static _readAttrs(el) {
		const bindtype = el.getAttribute('data-clientstate-bindtype');
		if (bindtype !== 'twoway') return null;

		const type = el.getAttribute('data-hasclientstate'); // 'value' | 'arrayitem'
		const name = el.getAttribute('data-clientstate-name') || el.name;
		const format = el.getAttribute('data-clientstate-format') || el.getAttribute('data-format') || null;

		const result = { type, name, format };

		if (type === 'arrayitem') {
			const index = el.getAttribute('data-clientstate-index');
			const field = el.getAttribute('data-clientstate-field');
			const key = el.getAttribute('data-clientstate-key');
			const keyfield = el.getAttribute('data-clientstate-keyfield') || 'id';

			if (index != null) result.index = parseInt(index, 10);
			if (field != null) result.field = field;
			if (key != null) {
				result.key = key;
				result.keyfield = keyfield;
			}
		}

		return result;
	}

	// ──────────────────────────────────────────────
	//  State access: read / write
	// ──────────────────────────────────────────────

	static _getStateValue(state, attrs) {
		if (attrs.type === 'arrayitem') {
			const arr = state[attrs.name];
			if (!arr) return null;
			const idx = InlineEditor._resolveIndex(arr, attrs);
			if (idx < 0 || idx >= arr.length) return null;
			return attrs.field ? arr[idx][attrs.field] : arr[idx];
		}
		return state[attrs.name];
	}

	static _setStateValue(state, attrs, value) {
		if (attrs.type === 'arrayitem') {
			const arr = state[attrs.name];
			if (!arr) return;
			const idx = InlineEditor._resolveIndex(arr, attrs);
			if (idx < 0 || idx >= arr.length) return;
			if (attrs.field) {
				arr[idx][attrs.field] = value;
			} else {
				arr[idx] = value;
			}
			return;
		}
		state[attrs.name] = value;
	}

	static _resolveIndex(arr, attrs) {
		if (attrs.key != null) {
			for (let k = 0; k < arr.length; k++) {
				if (arr[k] && arr[k][attrs.keyfield] === attrs.key) return k;
			}
			return -1;
		}
		return (attrs.index != null) ? attrs.index : -1;
	}

	// ──────────────────────────────────────────────
	//  Format parsing helpers
	// ──────────────────────────────────────────────

	/**
	 * Parse format string into components.
	 * Formats:  "percent:D" | "number:D" | "number:D:k" | "number:D:m"
	 * Returns { type: 'percent'|'number', decimals: N, multiplier: 1|1000|1000000 }
	 */
	static _parseFormat(format) {
		if (!format) return { type: 'number', decimals: 2, multiplier: 1 };

		const parts = format.split(':');
		const type = parts[0]; // 'percent' | 'number'
		const decimals = parts.length > 1 ? parseInt(parts[1], 10) || 0 : 2;

		let multiplier = 1;
		if (parts.length > 2) {
			const suffix = parts[2].toLowerCase();
			if (suffix === 'k') multiplier = 1000;
			else if (suffix === 'm') multiplier = 1000000;
		}

		return { type, decimals, multiplier };
	}

	/**
	 * Convert raw state value to a string suitable for the input field.
	 * User edits in display units: percents as "8.2", thousands as "350".
	 */
	static _formatForEdit(rawValue, format) {
		if (rawValue == null) return '';

		const fmt = InlineEditor._parseFormat(format);

		if (fmt.type === 'percent') {
			return (rawValue * 100).toFixed(fmt.decimals);
		}

		return (rawValue / fmt.multiplier).toFixed(fmt.decimals);
	}

	/**
	 * Parse user input string back to a raw state value.
	 * Inverse of _formatForEdit:  "8.2" (percent:1) → 0.082,  "350" (number:0:k) → 350000
	 * Returns null if the string is not a valid number.
	 */
	static _parseValue(str, format) {
		// Strip commas, spaces, trailing percent sign
		const clean = str.replace(/[,%\s]/g, '');
		if (clean === '') return null;

		const num = parseFloat(clean);
		if (isNaN(num)) return null;

		const fmt = InlineEditor._parseFormat(format);

		if (fmt.type === 'percent') {
			return num / 100;
		}

		return num * fmt.multiplier;
	}
}
